import React, { useMemo, useState } from 'react';
import { Modal, Select, Typography, Alert, Space, Button, Tag, DatePicker, TimePicker } from 'antd';
import { useQuery, useMutation } from '@apollo/client';
import { GET_PROVIDERS, GET_APPOINTMENT } from '../graphql/queries';
import { UPDATE_APPOINTMENT } from '../graphql/mutations';
import { useTranslation } from '../hooks/useTranslation';
import { getSpecializationLabel } from '../constants/specializations';
import dayjs, { Dayjs } from 'dayjs';

const { Text } = Typography;

interface ProviderSelectionModalProps {
  visible: boolean;
  appointmentId: string;
  onCancel: () => void;
  onAssigned: () => void;
}

interface ProviderDoctor {
  id: string;
  first_name_en?: string;
  last_name_en?: string;
  first_name_zh_hk?: string;
  last_name_zh_hk?: string;
  first_name_zh_cn?: string;
  last_name_zh_cn?: string;
  specialization?: string;
  title?: string;
}

interface ProviderClinic {
  id: string;
  name_en?: string;
  name_zh_hk?: string;
  name_zh_cn?: string;
  internal_name?: string;
}

interface ProviderSchedule {
  id: string;
  status?: string;
  is_available_for_telemedicine?: boolean;
}

interface ProviderItem {
  id: string;
  doctor?: ProviderDoctor;
  clinic?: ProviderClinic;
  schedules?: ProviderSchedule[];
}

const ProviderSelectionModal: React.FC<ProviderSelectionModalProps> = ({ visible, appointmentId, onCancel, onAssigned }) => {
  const { t, currentLanguage } = useTranslation();
  const { data, loading, error } = useQuery<{ providers: ProviderItem[] }>(GET_PROVIDERS);
  const { data: appointmentData } = useQuery<{ appointment: any }>(GET_APPOINTMENT, {
    variables: { id: appointmentId },
    skip: !visible
  });

  const [selectedProviderId, setSelectedProviderId] = useState<string | undefined>();
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(null);
  const [updateAppointment, { loading: assigning } ] = useMutation(UPDATE_APPOINTMENT);

  // Parse expected_time_range to get default date and time
  const defaultDateTime = useMemo(() => {
    if (!appointmentData?.appointment?.expected_time_range) return { date: null, time: null };
    
    const timeRange = appointmentData.appointment.expected_time_range;
    // Expected format: YYYY-MM-DD HH:mm-HH:mm
    const match = timeRange.match(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2})-\d{2}:\d{2}$/);
    if (match) {
      const dateStr = match[1];
      const timeStr = match[2];
      return {
        date: dayjs(dateStr),
        time: dayjs(`2000-01-01 ${timeStr}`)
      };
    }
    return { date: null, time: null };
  }, [appointmentData?.appointment?.expected_time_range]);

  // Set default values when appointment data changes
  React.useEffect(() => {
    if (defaultDateTime.date && !selectedDate) {
      setSelectedDate(defaultDateTime.date);
    }
    if (defaultDateTime.time && !selectedTime) {
      setSelectedTime(defaultDateTime.time);
    }
  }, [defaultDateTime, selectedDate, selectedTime]);

  const telemedicineProviders = useMemo(() => {
    const providers = data?.providers ?? [];
    return providers.filter(p => (p.schedules || []).some(s => s.is_available_for_telemedicine));
  }, [data]);

  const providerLabel = (p: ProviderItem) => {
    const d = p.doctor;
    const c = p.clinic;
    const nameEn = d ? `${d.first_name_en ?? ''} ${d.last_name_en ?? ''}`.trim() : '';
    const nameZhHk = d ? `${d.last_name_zh_hk ?? ''}${d.first_name_zh_hk ?? ''}`.trim() : '';
    const nameZhCn = d ? `${d.last_name_zh_cn ?? ''}${d.first_name_zh_cn ?? ''}`.trim() : '';
    // Use internal_name for clinic display
    const clinicName = c?.internal_name || c?.name_zh_hk || c?.name_en || '';

    let name = nameEn;
    if (currentLanguage === 'zh-HK') {
      name = nameZhHk || nameEn;
    } else if (currentLanguage === 'zh-CN') {
      name = nameZhCn || nameEn;
    }

    return `${name}${clinicName ? ` • ${clinicName}` : ''}`;
  };

  const isAppointmentType = appointmentData?.appointment?.reservation_type === 'appointment';
  const isFormValid = selectedProviderId && (!isAppointmentType || (selectedDate && selectedTime));

  const handleOk = async () => {
    if (!isFormValid) return;
    
    const updateData: any = {
      provider_id: selectedProviderId,
      status: 'confirmed',
      confirmed_at: new Date().toISOString(),
      reservation_type: appointmentData?.appointment?.reservation_type, // Preserve existing reservation_type
    };

    // Add date and time for appointment type
    if (isAppointmentType && selectedDate && selectedTime) {
      updateData.date = selectedDate.format('YYYY-MM-DD');
      updateData.time = selectedTime.format('HH:mm');
    }

    await updateAppointment({
      variables: {
        id: appointmentId,
        updateAppointmentDto: updateData
      }
    });
    onAssigned();
  };

  return (
    <Modal
      open={visible}
      title={t('appointments.assignProvider')}
      onCancel={onCancel}
      onOk={handleOk}
      okButtonProps={{ disabled: !isFormValid, loading: assigning }}
      destroyOnClose
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Text type="secondary">{t('appointments.onlyTelemedicineProviders')}</Text>
        {error && (
          <Alert type="error" message={t('common.error')} description={error.message} />
        )}
        <Select
          showSearch
          placeholder={t('appointments.selectProvider')}
          loading={loading}
          value={selectedProviderId}
          onChange={setSelectedProviderId}
          style={{ width: '100%' }}
          optionFilterProp="label"
          options={telemedicineProviders.map(p => ({
            value: p.id,
            label: providerLabel(p),
          }))}
        />
        
        {/* Date and Time Selection for Appointment Type */}
        {isAppointmentType && (
          <>
            <div>
              <Text strong>{t('appointments.date')}:</Text>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('appointments.date')}
                format="YYYY-MM-DD"
              />
            </div>
            <div>
              <Text strong>{t('appointments.time')}:</Text>
              <TimePicker
                value={selectedTime}
                onChange={setSelectedTime}
                style={{ width: '100%', marginTop: 8 }}
                placeholder={t('appointments.time')}
                format="HH:mm"
                minuteStep={5}
              />
            </div>
          </>
        )}
        
        <div>
          <Tag color="purple">{t('appointments.types.telemedicine')}</Tag>
          {isAppointmentType && (
            <Tag color="blue">{t('appointments.reservationTypes.appointment')}</Tag>
          )}
        </div>
        <Button type="link" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </Space>
    </Modal>
  );
};

export default ProviderSelectionModal; 